import React from 'react'
import * as moment from 'moment'
import { Spinner } from '../../../atoms'
import styled, { createGlobalStyle } from 'styled-components'
import SvgIcon from 'components/atoms/SvgIcon'
import { postService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import I18n from '../../../../core/i18n'
import { ReservationMultiDatePicker } from './atoms/ReservationMultiDatePicker'
import { ReservationStartTimes } from './atoms/ReservationStartTimes'
import { ReservationSelectedDatesDate } from './atoms/ReservationSelectedDatesDate'
import { getAges, ISelectedGuests } from 'utils/reservation'
import {
  getAvailableReservationDates,
  getReservationPostOptions,
} from 'utils/reservationDateOptions'
import { PrivateGroupOption } from './atoms/PrivateGroupOption'
import Typography from 'components/atoms/_layouts/Typography'
import { FullScreenReservationLayout } from 'components/pages/_layouts/fullscreenReservationLayout'
import {
  ReservationFullScreenModalHeaderContent,
  ReservationModalType,
} from 'components/molecules/Reservation/ReservationFullScreenModalHeader'
import { IJsonResponse } from 'core/JsonApiSerializer'
import {
  CALENDAR_EVENTS_DEFAULT_MONTHS,
  CALENDAR_EVENTS_INITIAL_MONTHS,
  IDate,
  useCalendarEvents,
} from 'utils/useCalendarEvents'
import { getSearchFiltersCookie, updateSearchFiltersCookie } from 'utils/searchFiltersCookie'
import {
  getReservationStorageData,
  updateReservationStorageData,
} from 'utils/reservationSessionStorage'
import { resolveInitialReservationParams } from 'utils/reservationInitialParams'

declare var window: interfaces.IWindow

// 未選択時は6ヶ月分の空き枠が対象になり得るため、初回は先頭のみマウントし
// スクロールに応じて追記する(初回DOM量をデータ総量から切り離す)。
const INITIAL_VISIBLE_DAYS = 5
const LOAD_MORE_DAYS_STEP = 5

const getInitialDatesFromUrl = (): moment.Moment[] => {
  if (typeof window === 'undefined') {
    return []
  }

  const urlParams = new URLSearchParams(window.location.search)
  const datesParam = urlParams.get('dates')

  if (!datesParam) {
    return []
  }

  try {
    const parsedDates = JSON.parse(datesParam)
    if (!Array.isArray(parsedDates)) {
      return []
    }

    return parsedDates
      .map(date => moment(date))
      .filter(date => moment.isMoment(date) && date.isValid())
  } catch {
    return []
  }
}

const getInitialGuestsFromUrl = (): ISelectedGuests | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const urlParams = new URLSearchParams(window.location.search)
  const guestCountsParam = urlParams.get('guest_counts')

  if (guestCountsParam) {
    try {
      const parsedGuestCounts = JSON.parse(guestCountsParam)
      if (Array.isArray(parsedGuestCounts)) {
        const [adults, children, infants] = parsedGuestCounts
        return {
          adults: adults ?? constants.DEFAULT_SEARCH_FILTER_GUESTS.adults,
          children: children ?? constants.DEFAULT_SEARCH_FILTER_GUESTS.children,
          infants: infants ?? constants.DEFAULT_SEARCH_FILTER_GUESTS.infants,
        }
      }
    } catch {
      // 何もしない。次の guest 数値パラメーターを試す。
    }
  }

  const guestsParam = urlParams.get('guests')
  if (guestsParam) {
    const guests = Number(guestsParam)
    if (!Number.isNaN(guests) && guests > 0) {
      return {
        adults: guests,
        children: 0,
        infants: 0,
      }
    }
  }

  return null
}

interface IWrapProps {
  user: interfaces.IUser
  isSignedIn: boolean
  post: IJsonResponse
  isOwner: boolean
  non_member_can_buy: boolean
  member_posts: any
}

interface IProps {
  isOwner: boolean
  isSignedIn: boolean
  post: any
  selectedDate?: moment.Moment | null
  user: interfaces.IUser
  non_member_can_buy: boolean
  member_posts: any
  initParams: {
    dates: moment.Moment[] | null
    guests: ISelectedGuests
  }
  initDate: moment.Moment
  setSelectedDate?(selectedDate: moment.Moment | null): void
}

const NoDates = ({ label }: { label: string }) => {
  return (
    <div className="no-dates">
      <Typography type="subtitle" size="xl">
        <SvgIcon name="busy" className="icon" ariaHidden={true} role="presentation" />
        {label}
      </Typography>
    </div>
  )
}

const WrapSelectDate: React.FC<IWrapProps> = props => {
  const reservationStorageData = getReservationStorageData()
  const searchFiltersCookie = getSearchFiltersCookie()
  const initialDatesFromUrl = getInitialDatesFromUrl()
  const initialGuestsFromUrl = getInitialGuestsFromUrl()

  // 予約導線中はsessionStorageを最新値として利用し、URLとCookieはフォールバックにする。
  const { dates: initialDates, guests: initialGuests } = resolveInitialReservationParams({
    reservationStorageData,
    initialDatesFromUrl,
    initialGuestsFromUrl,
    searchFiltersCookie,
  })

  const initParams = {
    dates: initialDates,
    guests: initialGuests,
  }
  const initDate = React.useMemo(() => {
    return initParams.dates?.length ? initParams.dates[0] : moment()
  }, [initParams.dates])

  // Transform post data from JSON to normal object
  const { post } = postService.getPostFromJson(props.post)
  const { data: member_posts } = postService.getDataFromJson(props.member_posts)
  const [selectedDate, setSelectedDate] = React.useState<moment.Moment | null>(initDate)
  return (
    <SelectDate
      {...props}
      initParams={initParams}
      post={post}
      initDate={initDate}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      member_posts={member_posts}
    />
  )
}

const SelectDate: React.FC<IProps> = ({ initDate, selectedDate, ...props }) => {
  const [selectedModalDate] = React.useState<moment.Moment | null>(selectedDate)
  const [dates, setDates] = React.useState<moment.Moment[]>(
    props.initParams?.dates?.map(date => moment(date)) ?? []
  )
  const [guests, setGuests] = React.useState<ISelectedGuests>(
    props.initParams?.guests ?? constants.DEFAULT_SEARCH_FILTER_GUESTS
  )
  const [selectedDates, setSelectedDates] = React.useState<IDate[]>([])
  const guestCount = React.useMemo(() => guests.adults + guests.children, [
    guests.adults,
    guests.children,
  ])
  const { calendarEvents, fetching, availableDates, getCalendarEvents } = useCalendarEvents()

  // 初期表示時にカレンダーイベントを分割で取得する
  React.useEffect(() => {
    const diffDate = initDate.clone().add(CALENDAR_EVENTS_DEFAULT_MONTHS, 'months')
    const diff = CALENDAR_EVENTS_INITIAL_MONTHS - CALENDAR_EVENTS_DEFAULT_MONTHS
    const fetchCalendarEvents = async () => {
      await getCalendarEvents(props.post.id, initDate, CALENDAR_EVENTS_DEFAULT_MONTHS)
      await getCalendarEvents(props.post.id, diffDate, diff)
    }
    fetchCalendarEvents()
  }, [initDate])

  // 日付が変わったときに dateRange を Cookie に保存する
  React.useEffect(() => {
    if (dates.length > 0) {
      const sortedDates = [...dates].sort((a, b) => a.diff(b))
      updateSearchFiltersCookie({
        dateRange: {
          startDate: sortedDates[0].format('YYYY-MM-DD'),
          endDate: sortedDates[sortedDates.length - 1].format('YYYY-MM-DD'),
        },
      })
    } else if (dates.length === 0) {
      // 予約画面で日付が未選択でも、検索ヘッダーの日付は保持する。
      updateSearchFiltersCookie({ dateRange: null })
    }
  }, [dates])

  const getCalendarEventsInReservationDatePicker = async (newDate: moment.Moment) => {
    await getCalendarEvents(props.post.id, newDate, CALENDAR_EVENTS_DEFAULT_MONTHS)
  }

  const selectedDayStartTimesForDate = date => {
    if (!calendarEvents || !date) {
      return null
    }

    const day = calendarEvents.find(calendarEvent => date.isSame(calendarEvent.date, 'day'))
    if (day) {
      // check start times is setup for this post
      const ok_days = []
      day.start_times.forEach(item => {
        const memberPost = props.member_posts.find(mp => String(mp.id) === String(item.post_id))
        if (!memberPost) {
          return
        }
        if (
          !!memberPost.bokun_activity_id ||
          memberPost.post_start_times.map(time => time.start_time).includes(item.start_time)
        ) {
          ok_days.push(item)
        }
      })

      return ok_days
    } else {
      return []
    }
  }

  React.useEffect(() => {
    const ok_days = dates.map(date => {
      const starTimes = selectedDayStartTimesForDate(date)
      return {
        date,
        startTimes: starTimes ?? [],
      }
    })
    setSelectedDates(ok_days)
    // 初期化
  }, [calendarEvents, dates])

  const checkValidated = React.useCallback(() => {
    const { isOwner, user } = props
    const needFlashMessage = isOwner || user?.agent || user?.guide
    if (needFlashMessage) {
      const message = isOwner
        ? I18n.t('post.cannot_reserve_own_post')
        : I18n.t('post.cannot_reserve_agent')
      window.flashMessages.addMessage({
        text: message,
        type: 'error',
      })

      return false
    }

    return moment.isMoment(selectedModalDate)
  }, [props, selectedModalDate])

  const showPaymentForm = React.useCallback(
    (memberPost, reservation?: any) => {
      if (checkValidated() && selectedModalDate) {
        const newReservation = reservation ?? {
          selectedDate: selectedModalDate.toISOString(),
          totalPrice: null,
          departure: null,
        }

        updateReservationStorageData({
          guests,
          memberPost,
          reservation: newReservation,
        })
        window.location.href = `/posts/${props?.post?.slug}/reservations/payment`
      }
    },
    [checkValidated, selectedModalDate, guests, props?.post?.slug]
  )

  const postOptions = React.useMemo(() => getReservationPostOptions(props.member_posts, guests), [
    props.member_posts,
    guests,
  ])

  const currentDates = React.useMemo(() => {
    const updatedDates = (selectedDates ?? []).length > 0 ? selectedDates : availableDates
    return getAvailableReservationDates(updatedDates, postOptions, guestCount)
  }, [selectedDates, availableDates, postOptions, guestCount])

  const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_DAYS)

  // 選択条件が変わったら表示件数を初期化する(月送りによる setDates([]) も含む)
  React.useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_DAYS)
  }, [dates, guestCount])

  const visibleDates = React.useMemo(() => currentDates.slice(0, visibleCount), [
    currentDates,
    visibleCount,
  ])

  const hasMoreDates = visibleCount < currentDates.length
  const loadMoreSentinelRef = React.useRef<HTMLDivElement | null>(null)

  // 末尾の番兵が見えたら次の N 日分を追記マウントする(初回DOM量を抑える無限スクロール)
  // 依存は hasMoreDates のみ。currentDates を入れるとデータ更新のたびに observer を作り直し、
  // 視界内の番兵を再 observe → 初回コールバックで連鎖的に読み込んでしまうため。
  React.useEffect(() => {
    const sentinel = loadMoreSentinelRef.current
    if (!sentinel || !hasMoreDates || typeof IntersectionObserver === 'undefined') {
      return
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount(prev => prev + LOAD_MORE_DAYS_STEP)
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMoreDates])

  return (
    <>
      <GlobalStyle />
      <FullScreenReservationLayout
        headerContent={
          <ReservationFullScreenModalHeaderContent
            type={ReservationModalType.StartDate}
            closeHandle={() => history.back()}
          />
        }
      >
        <ReservationCalendar>
          <div className="Info">
            <ReservationMultiDatePicker
              post={props.post}
              initialDate={dates.length > 0 ? dates[0] : moment()}
              dates={dates}
              setDates={setDates}
              guests={guests}
              guestCount={guestCount}
              setGuests={setGuests}
              calendarEvents={calendarEvents}
              getCalendarEvents={getCalendarEventsInReservationDatePicker}
              isPrivate={props?.post?.post_type === 'private'}
              ages={getAges(props?.post?.post_prices)}
            />
            {!props.isSignedIn && !props.non_member_can_buy && (
              <div className="need_signin">You need to be signed in to book this tour</div>
            )}
            <div className="privateGroupOption">
              <PrivateGroupOption />
            </div>
          </div>

          <div className="SelectedDates">
            <div className="datesAndTimes">
              {!fetching && currentDates.length === 0 && (
                <NoDates label="Sorry! No dates available for selection" />
              )}
              {visibleDates.map((date, index) => (
                <div key={date.date.format('YYYY-MM-DD')} className="SelectedDates_wrap">
                  {/* Only display current month selections */}
                  {date.startTimes && (
                    <ReservationSelectedDatesDate date={date} post={props.post} />
                  )}
                  <div className="StartTimes_List">
                    {date.startTimeGroups.map(group => (
                      <ReservationStartTimes
                        key={`${date.date.format('YYYY-MM-DD')}-${group.postId}`}
                        date={date}
                        guests={guests}
                        post={props.post}
                        memberPost={group.memberPost}
                        startTimes={group.startTimes}
                        isPostInFamily={props.post.family_owner}
                        nonMemberCanBuy={props.non_member_can_buy}
                        initialOpen={index === 0}
                        showPaymentForm={showPaymentForm}
                      />
                    ))}
                  </div>
                </div>
              ))}
              {hasMoreDates && <div ref={loadMoreSentinelRef} className="LoadMoreSentinel" />}
              {fetching && <Spinner />}
            </div>
          </div>
        </ReservationCalendar>
      </FullScreenReservationLayout>
    </>
  )
}

const GlobalStyle = createGlobalStyle`
  .header-wrapper, .Scroller, .mainFooter {
    display: none !important;
  }
`

const ReservationCalendar = styled.div`
  width: 100%;
  max-width: 112rem;
  margin: 0 auto;
  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    gap: 2.4rem;
  }
  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    padding: 0 15px;
  }
  .header_content {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    -webkit-box-align: center;
    align-items: center;
    margin: 0px auto;
  }
  .Info {
    position: fixed;
    width: 38.8%;
    max-width: 56rem;
    margin-top: 6.4rem;
    z-index: 99;
    @media (max-width: ${constants.BREAKPOINT_DESKTOP_XL}px) {
      width: 44.8%;
    }
    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      position: relative;
      width: 100%;
      max-width: 56rem;
      margin-top: 4.4rem;
      margin-left: auto;
      margin-right: auto;
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      margin-top: 2.4rem;
    }
    .privateGroupOption {
      margin-top: 4rem;
      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        margin-top: 2.4rem;
      }
    }
  }

  .need_signin {
    font-size: 12px;
    font-weight: bold;
    padding-top: 16px;
    color: ${constants.COLORS.subText};
  }

  .SelectedDates {
    width: 42.8%;
    min-height: 480px;
    max-width: 480px;
    padding: 0 0 4rem;
    margin: 3.2rem 0 0 auto;
    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      padding: 0.8rem 0 2.4rem;
      width: 100%;
      max-width: 560px;
      margin-right: auto;
      margin-top: 2.4rem;
    }
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      margin-top: 0.4rem;
    }

    .SelectedDates_wrap {
      margin-bottom: 2.4rem;
    }

    .datesAndTimes {
      height: 100%
      // max-height: calc(100dvh - 140px);
      // overflow: auto;
      // @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        // max-height: none;
      // }
    }
    .no-dates {
      background-color: ${constants.COLORS.cardBG};
      border: 1px solid ${constants.COLORS.disabled};
      border-radius: 0.8rem;
      > div {
        display: flex;
        width: 100%;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1.6rem;
        padding: 6.4rem 0;
        .icon {
          width: 6.4rem;
          height: 6.4rem;
          padding: 1.6rem;
          color: ${constants.COLORS.subText};
          background-color: ${constants.COLORS.white};
          border: 1px solid ${constants.COLORS.disabled};
          border-radius: 50%;
        }
      }
    }
  }

  .StartTimes_List {
    display: flex;
    flex-direction: column;
    gap: 24px;
    > div {
      margin-left: 0;
      margin-right: 0;
    }
  }
`

export default WrapSelectDate
